import { KnownUpdateSettingsSchemaDtoKind } from '@groundfloor-platform/mgmt-service-g-sdk';
import { useCallback, useMemo } from 'react';
import { useQueryGetProjectChildProjects } from '../../../../core/queries/projects.queries';
import { useMutationCreateRegistrationPage } from '../../../registration-pages/queries';
import { useQueryListSettingsSchemas } from '../../../settings-schemas/queries';
import { EntityForm } from '../form.types';
import { RegistrationPageForm } from './form';
import { RegistrationPageFormOutput } from './form-output';
import { RegistrationPageFormSchema } from './form-schema';
import { registrationPageFormUISchema } from './ui-schema';

export function useCreateRegistrationPageForm(
  projectID: string | undefined,
  parentNodeId: string | undefined
): EntityForm | null {
  // We expect to be able to find the parent project, becuase in order
  // to create a project, the user must have been able to select the parent
  // project from the tree.
  const childProjects = useQueryGetProjectChildProjects(projectID);
  const registrationPageMutation = useMutationCreateRegistrationPage();
  const settingsSchemas = useQueryListSettingsSchemas();

  const childProjectData = useMemo(() => {
    return (childProjects.data || []).filter((p) => p.id !== projectID);
  }, [childProjects.data, projectID]);

  const registrationPageSchema = useMemo(() => {
    return settingsSchemas.data?.find(
      (schema) => schema.kind === KnownUpdateSettingsSchemaDtoKind.RegistrationPage && schema.name === 'default'
    );
  }, [settingsSchemas.data]);

  const initialFormOutput = useMemo(() => {
    return new RegistrationPageFormOutput({});
  }, []);

  const formSchema = useMemo(() => {
    return RegistrationPageFormSchema.FromSettingsSchema(childProjectData, 'create', registrationPageSchema);
  }, [childProjectData, registrationPageSchema]);

  const onSubmit = useCallback(
    async (formOutput: RegistrationPageFormOutput) => {
      if (!projectID || !registrationPageSchema?.id) {
        return;
      }
      const result = await registrationPageMutation.mutateAsync({
        projectID,
        settingsSchemaID: registrationPageSchema.id,
        settings: RegistrationPageFormOutput.ToCreateSettingsDto(formOutput)
      });
      return {
        entityId: result.id,
        parentNodeId
      };
    },
    [parentNodeId, projectID, registrationPageMutation, registrationPageSchema?.id]
  );

  if (!formSchema) {
    return null;
  }

  return {
    isLoading: childProjects.isLoading || settingsSchemas.isLoading,
    title: 'Registration Page Settings',
    initialFormOutput: initialFormOutput,
    formSchema,
    uiSchema: registrationPageFormUISchema,
    getOutput(formData) {
      return new RegistrationPageFormOutput(formData as RegistrationPageForm);
    },
    onSubmit,
    isLocked: false,
    lockedMessage: null,
    defaultValues: {}
  };
}
